/**
 * Heading primitives.
 *
 * - Type ramp baked in (responsive sm/lg breakpoints).
 * - `tone` defaults to "default" (theme-aware text colour).
 * - `as` lets you render an `<h2>`-styled element as another tag (e.g. `<p>`)
 *   without losing the visual scale — useful for SEO when you only want one
 *   real `<h1>` per page.
 * - `H3` accepts `size?: 'sm' | 'md' | 'lg'`, covering the three real
 *   personalities seen across consumers:
 *     - `sm` — column header / pricing tier name (`text-lg font-semibold`)
 *     - `md` (default) — card / feature title (`text-2xl lg:text-3xl font-light`)
 *     - `lg` — long-form story / "how it works" step (`text-3xl lg:text-4xl font-light`)
 * - All heading utilities default to `font="display"` (Space Grotesk via the
 *   `--font-display` CSS var the consumer wires up with `next/font`). Pass
 *   `font="sans"` to flip an individual heading to Inter (e.g. tier names in
 *   a pricing card grid), or `font="inherit"` to let a wrapping `font-*` div
 *   take over.
 *
 * Server-component safe (no hooks, no client-only APIs).
 */

import React from 'react'
import { Join } from '../../lib/utils'
import { resolveFontClass, toneClass, type FontProp, type Tone } from './tokens'

type AnyHeading = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'p' | 'span'

export interface HeadingProps {
  children: React.ReactNode
  tone?: Tone
  as?: AnyHeading
  className?: string
  id?: string
  /** Font family override. Defaults to `'display'` (Space Grotesk). Use
   *  `'inherit'` inside a `font-*` parent to skip emitting any font class. */
  font?: FontProp
}

export interface H3Props extends HeadingProps {
  size?: 'sm' | 'md' | 'lg'
}

const baseHeading = 'tracking-tight text-balance'

// Type ramp.
//
// Mirrors the codelogician.dev hero pattern — large display sizes paired with
// genuinely thin weights, hierarchy carried by SIZE more than by weight.
//
// Notes:
// - H1 jumps `sm:text-6xl` → `lg:text-[80px]` (no intermediate `lg:text-7xl`
//   step). This matches the actual codelogician.dev hero, where the 80 px
//   bump kicks in at the laptop breakpoint, not at xl.
// - H2 starts at `text-4xl` even on mobile because every real consumer H2
//   does (the previous `text-3xl` mobile baseline was too small).
// - Note on weights: Space Grotesk (the design-studio default display font)
//   only ships 300–700 on Google Fonts, so `font-thin` (100) on H1 is browser-
//   synthesised. That matches what codelogician.dev does today and renders
//   well on modern displays. Consumers can override to a different family
//   that ships a real 100/200 weight, or override `className` per call site.
const ramp = {
  h1: 'text-4xl font-thin leading-snug sm:text-6xl lg:text-[80px]',
  h2: 'text-4xl font-light leading-snug lg:text-5xl',
  h4: 'text-lg font-semibold leading-snug',
} as const

const h3Size = {
  sm: 'text-lg font-semibold leading-snug',
  md: 'text-2xl font-light leading-snug lg:text-3xl',
  lg: 'text-3xl font-light leading-snug lg:text-4xl',
} as const

function makeHeading(defaultTag: AnyHeading, scale: keyof typeof ramp) {
  const Component = ({
    children,
    tone = 'default',
    font = 'display',
    as,
    className,
    id,
  }: HeadingProps) => {
    return React.createElement(
      as ?? defaultTag,
      {
        id,
        className: Join(
          resolveFontClass(font),
          baseHeading,
          ramp[scale],
          toneClass[tone],
          className,
        ),
      },
      children,
    )
  }
  Component.displayName = `H${scale.replace('h', '').toUpperCase()}`
  return Component
}

export const H1 = makeHeading('h1', 'h1')
export const H2 = makeHeading('h2', 'h2')
export const H4 = makeHeading('h4', 'h4')

export function H3({
  children,
  tone = 'default',
  font = 'display',
  size = 'md',
  as,
  className,
  id,
}: H3Props) {
  return React.createElement(
    as ?? 'h3',
    {
      id,
      className: Join(
        resolveFontClass(font),
        baseHeading,
        h3Size[size],
        toneClass[tone],
        className,
      ),
    },
    children,
  )
}
